import CalendarLeftNav from './CalendarLeftNav';
import CalendarModuleCommanding from './CalendarModuleCommanding';
import CalendarModuleContent from './CalendarModuleContent';
import CalendarModuleHotkeysMap from './CalendarModuleHotkeysMap';
import ModuleRenderer from './ModuleRenderer';
import { useCalendarModuleHotkeys } from '../utils/bindCalendarModuleHotKeys';
import { observer } from 'mobx-react-lite';
import { OWAAccountsLoadError } from 'owa-calendar-accounts-error-dialog';
import { LazyDebugPanel } from 'owa-calendar-debug-ui';
import { initializeFocusController } from 'owa-calendar-focus-manager';
import { store as moduleCommandStore } from 'owa-calendar-module-command';
import { NotificationBarHost } from 'owa-calendar-notification-bar';
import { CalendarProjectionPopoutHost } from 'owa-calendar-projection-popouthost';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { RoamingTimeZoneNotification } from 'owa-roaming-time-zone-dialog';
import type { SearchBoxContainerHandle } from 'owa-search';
import type { SkipLinkPlaceholderHandle } from 'owa-skip-link';
import * as React from 'react';
import {
    isBrowserChrome,
    isBrowserEDGECHROMIUM,
    isBrowserFirefox,
    isBrowserSafari,
} from 'owa-user-agent';
import { shouldCommandingStretchLeft } from 'owa-calendar-layout';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import { CommandingViewMode } from 'owa-outlook-service-option-store/src/store/schema/options/CommandingOptions';

const styles = require('./CalendarModule.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CalendarModuleProps {
    searchBoxRef: React.RefObject<SearchBoxContainerHandle>;
    skipLinkRef: React.RefObject<SkipLinkPlaceholderHandle>;
}

export default observer(function CalendarModule(props: CalendarModuleProps) {
    React.useEffect(() => {
        /**
         * Here we initialize the pane focus controller for the calendar module. This controller will listen for new popups and manually
         * handle setting the focus when popups open and close.
         *
         * When creating a new event, the focus needs to go back to the "New" button.
         * When exiting a search via Escape hotkey, the focus needs to go back to the calendar content
         */
        initializeFocusController();
    }, []);

    useCalendarModuleHotkeys(props.skipLinkRef, props.searchBoxRef);

    // For Teams OPX scenario, we stitch together content, full compose, and read pane as full "views"
    // to simulate current Teams calendar design
    const shouldHideModuleContent =
        isHostAppFeatureEnabled('hideCalendarModuleContent') &&
        (moduleCommandStore.moduleCommand == 'Open_FullComposeForm' ||
            moduleCommandStore.moduleCommand == 'Open_ReadPanel');

    const isCalendarProjectionEnabled =
        isCalendarProjectionFlightEnabled() && isBrowserSupportProjection();

    const ModuleContent = (
        <>
            {!isHostAppFeatureEnabled('hideCalendarLeftNav') && <CalendarLeftNav />}
            <CalendarModuleContent />
            {isCalendarProjectionEnabled && <CalendarProjectionPopoutHost />}
            <OWAAccountsLoadError />
            {isFeatureEnabled('cal-roaming-timeZones') && <RoamingTimeZoneNotification />}
            <CalendarModuleHotkeysMap />
            {isFeatureEnabled('cal-dev') && <LazyDebugPanel />}
        </>
    );

    const ModuleContentWrapper = shouldCommandingStretchLeft() ? (
        <div className={styles.module}>
            {shouldCommandingStretchLeft() && <CalendarModuleCommanding />}
            <div
                className={classNames(
                    styles.moduleUnderHeader,
                    getIsViewModeSelected(CommandingViewMode.SingleLineRibbon) &&
                        styles.moduleUnderHeaderSLR,
                    getIsViewModeSelected(CommandingViewMode.MultiLineRibbon) &&
                        styles.moduleUnderHeaderMLR
                )}>
                {ModuleContent}
            </div>
        </div>
    ) : (
        <div className={styles.moduleCommandOverContentOnly}>{ModuleContent}</div>
    );

    return (
        <>
            {!shouldHideModuleContent && ModuleContentWrapper}
            <ModuleRenderer />
            <div style={{ zIndex: 3 }}>
                <NotificationBarHost hostId={'CalendarModuleNotificationBarHost'} />
            </div>
        </>
    );
});

function isCalendarProjectionFlightEnabled() {
    return (
        isFeatureEnabled('cal-cmp-popout-projection') && isFeatureEnabled('mail-popout-projection')
    );
}

function isBrowserSupportProjection() {
    return isBrowserChrome() || isBrowserFirefox() || isBrowserEDGECHROMIUM() || isBrowserSafari();
}
